// Help, Legal, About, the diet rules and how the diet plan is calculated (static text, localized).
import React from 'react';
import { Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { RootParams } from '../navigation';
import { useNav } from '../navigation';
import { useHeader } from '../hooks';
import { useAllIngredients } from '../store/library';
import { useSettings } from '../store/settings';
import { DietPill } from '../components/DietMark';
import { DIETS } from '../types';
import { font, radius, spacing, usePalette } from '../theme';
import { Body, Caption, ListItem, Screen, Section } from '../components/ui';
import { version } from '../../package.json';
import LegalLinks from '../components/LegalLinks';

export default function InfoScreen({ route }: NativeStackScreenProps<RootParams, 'Info'>) {
  const { t } = useTranslation();
  const nav = useNav();
  const page = route.params.page;
  useHeader({ title: t(`${page}.title`) }, [page, t]);
  if (page === 'dietRules') return <DietRules />;
  if (page === 'dietPlanMethod') {
    return (
      <Screen>
        <Body>{t('dietPlanMethod.intro')}</Body>
        {(['rest', 'activity', 'digestion', 'weight', 'nutrients', 'limits'] as const).map(k => (
          <Section key={k} title={t(`dietPlanMethod.${k}Title`)}>
            <Body secondary>{t(`dietPlanMethod.${k}`)}</Body>
          </Section>
        ))}
        <Section title={t('dietPlanMethod.sourcesTitle')}>
          <Caption>{t('dietPlanMethod.sources')}</Caption>
        </Section>
      </Screen>
    );
  }
  if (page === 'legal') {
    return (
      <Screen>
        <Body>{t('legal.body')}</Body>
        <Section><LegalLinks /></Section>
      </Screen>
    );
  }
  if (page === 'about') {
    return (
      <Screen>
        <Body>{t('about.body')}</Body>
        <Caption>{t('about.version', { version })}</Caption>
      </Screen>
    );
  }
  return (
    <Screen>
      <Body>{t('help.body')}</Body>
      <Section>
        <ListItem title={t('settings.legal')} icon="legal" onPress={() => nav.navigate('Info', { page: 'legal' })} />
        <ListItem title={t('settings.about')} icon="info" onPress={() => nav.navigate('Info', { page: 'about' })} />
      </Section>
      <Section title={t('help.tutorials')}>
        <ListItem title={t('settings.tutorial')} icon="sparkles" onPress={() => nav.navigate('Onboarding')} />
      </Section>
      <Section title={t('dietRules.title')}>
        <ListItem title={t('dietRules.title')} icon="diet" onPress={() => nav.navigate('Info', { page: 'dietRules' })} />
      </Section>
      <Section title={t('dietPlanMethod.title')}>
        <ListItem title={t('dietPlanMethod.title')} icon="nutrition" onPress={() => nav.navigate('Info', { page: 'dietPlanMethod' })} />
      </Section>
      <Section title={t('help.backups')}>
        <Body secondary>{t('help.backupsBody')}</Body>
      </Section>
      <Section title={t('settings.handsFree')}>
        <Body secondary>{t('settings.handsFreeBody')}</Body>
      </Section>
    </Screen>
  );
}

/**
 * How the diet check works, and for every diet its rules and exactly which ingredients the check
 * flags (read live from the catalog and the user's own ingredients), so every warning can be traced.
 */
function DietRules() {
  const { t } = useTranslation();
  const p = usePalette();
  const ingredients = useAllIngredients();
  const mine = useSettings(s => s.diets);
  const order = [...DIETS].sort((a, b) => Number(mine.includes(b)) - Number(mine.includes(a)));
  return (
    <Screen>
      <Body>{t('dietRules.intro')}</Body>
      <Section title={t('dietRules.howTitle')}>
        <Body secondary>{t('dietRules.how')}</Body>
        <Body secondary>{t('dietRules.confirm')}</Body>
        <Body secondary>{t('dietRules.later')}</Body>
      </Section>
      {order.map(d => {
        const flagged = Object.values(ingredients).filter(i => i.notDiet.includes(d)).map(i => i.title).sort((a, b) => a.localeCompare(b));
        return (
          <View key={d} style={{ backgroundColor: p.card, borderRadius: radius.l, padding: spacing.l, gap: spacing.s }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.s }}>
              <DietPill diets={[d]} size={15} color={p.secondaryText} />
              <Text style={{ flex: 1, fontSize: font.headline, fontWeight: '700', color: p.text }}>{t(`diets.${d}`)}</Text>
              {mine.includes(d) ? <Caption>{t('dietRules.yours')}</Caption> : null}
            </View>
            <Body>{t(`dietRules.rules.${d}`)}</Body>
            <Caption>{flagged.length ? t('dietRules.flagged', { items: flagged.join(', ') }) : t('dietRules.nothingFlagged')}</Caption>
            {d === 'kosher' ? <Caption>{t('dietRules.kosherDish')}</Caption> : null}
          </View>
        );
      })}
    </Screen>
  );
}
